import "antd/dist/reset.css";
import "react-toastify/dist/ReactToastify.css";
import "rsuite/dist/rsuite.min.css";
import { DatePicker } from "rsuite";
import { TimePicker } from "antd";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Select from "react-select";
import utc from "dayjs/plugin/utc";
import { useProviderClients } from "./Clients";
import ScheduleCheckInDrawer from "../components/ScheduleCheckInDrawer";

dayjs.extend(utc);

const ProviderAddAppointmentForm = ({
  fetchAppointmentData,
  selectedDate,
  selectedTime,
  setShowAddAppointment,
}) => {
  const user = useSelector((state) => state?.user);

  const [date, setDate] = useState(selectedDate || null);
  const [duration, setDuration] = useState(30);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [time, setTime] = useState("");
  const [timeValue, setTimeValue] = useState(null); // For Ant Design TimePicker
  const [isLoading, setIsLoading] = useState(false);

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;
  const currentUserProviderId = user.user?.providerId;

  const { data: providerClients, loading: clientsLoading } = useProviderClients({
    providerId: currentUserProviderId,
    businessId,
    businessIdentifier,
    page: 1,
    limit: 500,
    status: "Active",
  });

  const clientOptions = (providerClients || []).map((client) => ({
    value: client._id,
    label: client.name || "Unnamed client",
  }));

  useEffect(() => {
    if (selectedDate) {
      setDate(selectedDate);

      if (selectedTime) {
        const [hours, minutes] = selectedTime.split(":").map(Number);
        const timeString = `${String(hours).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")}`;
        setTime(timeString);
        setTimeValue(dayjs().hour(hours).minute(minutes));
        return;
      }

      if (selectedDate instanceof Date) {
        const hours = selectedDate.getHours();
        const minutes = selectedDate.getMinutes();

        if (hours !== 0 || minutes !== 0) {
          const timeString = `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}`;
          setTime(timeString);
          setTimeValue(dayjs().hour(hours).minute(minutes));
          return;
        }
      }
    }

    setTime("");
    setTimeValue(null);
  }, [selectedDate, selectedTime]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedUser) {
      toast.error(
        "Please select a client to schedule the session.",
        {
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
          hideProgressBar: false,
          pauseOnHover: true,
          position: "top-right",
        }
      );
      return;
    }

    if (!date || !time) {
      toast.error("Please select a date and time to schedule the session", {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        hideProgressBar: false,
        pauseOnHover: true,
        position: "top-right",
      });
      return;
    }

    if (!currentUserProviderId) {
      toast.error("Provider ID not found. Please try again.", {
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
        hideProgressBar: false,
        pauseOnHover: true,
        position: "top-right",
      });
      return;
    }

    setIsLoading(true);
    const authToken = user.user?.token;
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/appointment/create?businessId=${businessId}&businessIdentifier=${businessIdentifier}`,
        {
          date: date.toLocaleDateString("en-US"),
          duration,
          selectedProvider: currentUserProviderId,
          selectedUser: selectedUser.value,
          time,
          timeZone: clientTimeZone,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        fetchAppointmentData();
        setTimeout(() => {
          setShowAddAppointment(false);
        }, 2000);
      } else {
        toast.error("Could not create appointment.");
      }
    } catch (error) {
      console.error("🚀 -> handleSubmit -> error:", error);
      toast.error(error.response?.data?.message || "Failed to create appointment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowAddAppointment(false);
    setShowForm(false);
  };

  const handleDateChange = (newValue) => setDate(newValue);

  // Force z-index for picker popups when modal is open
  useEffect(() => {
    const forceZIndex = () => {
      const pickerPopups = document.querySelectorAll('.rs-picker-popup');
      pickerPopups.forEach(popup => {
        popup.style.zIndex = '10001';
      });
    };

    // Run immediately
    forceZIndex();

    // Set up a mutation observer to watch for new picker popups
    const observer = new MutationObserver(forceZIndex);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  if (!showForm) return null;

  return (
    <div style={containerStyle}>
      <form onSubmit={handleSubmit}>
        <div style={cardStyle}>
          {/* Title and Close Button */}
          <div style={headerStyle}>
            <h3 style={sectionTitleStyle}>MAKE NEW APPOINTMENT</h3>
            <button style={closeButtonStyle} onClick={handleClose}>
              ×
            </button>
          </div>
          {/* Client Selection */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>CLIENT</label>
            <Select
              isLoading={clientsLoading}
              isSearchable
              onChange={setSelectedUser}
              options={clientOptions}
              placeholder="Select Client"
              styles={selectStyle}
              value={selectedUser}
            />
          </div>
          
          {/* Date Selection */}
          <div style={formGroupStyle}>
            <label style={labelStyle}>Date</label>
            <DatePicker
              className="datepickerBorder"
              format="MM/dd/yyyy"
              onChange={handleDateChange}
              style={inputStyle}
              value={date}
            />
          </div>
          
          {/* Time and Duration Row */}
          <div style={formRowStyle}>
            <div style={{ flex: 1, marginRight: "10px" }}>
              <label style={labelStyle}>Start Time</label>
              <TimePicker
                format="h:mm a"
                hourStep={1}
                minuteStep={15}
                onChange={(value) => {
                  if (value) {
                    setTime(value.format("HH:mm"));
                    setTimeValue(value);
                  } else {
                    setTime("");
                    setTimeValue(null);
                  }
                }}
                style={inputStyle}
                use12Hours
                value={timeValue}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Duration</label>
              <select
                onChange={(e) => setDuration(e.target.value)}
                style={inputStyle}
                value={duration}
              >
                <option value="30">30 minutes</option>
                <option value="60">60 minutes</option>
              </select>
            </div>
          </div>

          {/* Submit Button */}
          <div style={buttonContainerStyle}>
            <button 
              type="submit" 
              style={isLoading ? buttonStyleDisabled : buttonStyle}
              disabled={isLoading}
            >
              {isLoading ? "Creating..." : "MAKE NEW APPOINTMENT"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const containerStyle = {
  fontFamily: "Poppins, sans-serif",
  margin: "50px auto",
  maxWidth: "450px",
};

const cardStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
  padding: "20px",
  width: "120%",
};

const headerStyle = {
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
};

const sectionTitleStyle = {
  color: "#333",
  fontFamily: "Poppins",
  fontSize: "16px",
  fontWeight: "700",
  textAlign: "left",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const closeButtonStyle = {
  alignItems: "center",
  border: "none",
  borderRadius: "50rem",
  color: "#808080",
  cursor: "pointer",
  display: "flex",
  fontSize: "14px",
  height: "25px",
  justifyContent: "center",
  width: "25px",
};

const formGroupStyle = {
  marginBottom: "20px",
};

const inputStyle = {
  backgroundColor: "#F4F4F6",
  border: "none",
  borderRadius: "8px",
  boxSizing: "border-box",
  color: "#808080",
  height: "45px",
  padding: "10px",
  width: "100%",
};

const formRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "20px",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "14px",
  fontWeight: "600",
  color: "#333",
  textTransform: "uppercase",
};

const buttonContainerStyle = {
  textAlign: "center",
  marginTop: "20px",
};

const buttonStyle = {
  backgroundColor: "#27B99A",
  border: "none",
  borderRadius: "8px",
  color: "white",
  cursor: "pointer",
  padding: "12px 24px",
  fontSize: "14px",
  fontWeight: "600",
  textTransform: "uppercase",
  width: "100%",
  transition: "all 0.2s",
};

const buttonStyleDisabled = {
  ...buttonStyle,
  backgroundColor: "#ccc",
  cursor: "not-allowed",
  opacity: 0.7,
};

const selectStyle = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "#F4F4F6",
    borderRadius: "8px",
    height: "45px",
  }),
};

const AddAppointment = ({
  fetchAppointmentData,
  selectedDate,
  selectedTime,
  setShowAddAppointment,
}) => {
  const user = useSelector((state) => state?.user);

  const isCareManager =
    user.user?.role === "careManager" ||
    user.user?.providerType === "Care Manager";

  if (isCareManager) {
    return (
      <ScheduleCheckInDrawer
        fetchAppointmentData={fetchAppointmentData}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        setShowAddAppointment={setShowAddAppointment}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <ProviderAddAppointmentForm
        fetchAppointmentData={fetchAppointmentData}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        setShowAddAppointment={setShowAddAppointment}
      />
    </div>
  );
};

export default AddAppointment;
